import { useEffect, useRef, useState } from "react";
import { makeContinuousEaseOut } from "../utils/easing";
import {
  SPIN_TOTAL_MS,
  DECELERATE_AT,
  MIN_PHASE_MS,
  TOTAL_TURNS,
  FAST_TURNS,
} from "../constants/animation";
import {
  normalizeAngleDeg,
  shortestPositiveDelta,
  findSectionIndexById,
  isWinningSection,
} from "../utils/math";
import {
  FORTUNE_WHEEL_STEPS,
  type FortuneWheelSectionItem,
} from "../../../type/FortuneWheel";
import { useFortuneWheelStore } from "../../../store/fortuneWheel";

const TOP_OFFSET_DEG = 0;
const CLOCKWISE = true;

export const useWheelSpin = () => {
  const sections = useFortuneWheelStore((s) => s.sectionList);
  const canSpin = useFortuneWheelStore((s) => s.isAvailable);
  const winner = useFortuneWheelStore((s) => s.prizeSection);

  const step = useFortuneWheelStore((s) => s.step);
  const setStep = useFortuneWheelStore((s) => s.setStep);

  const [angleDeg, setAngleDeg] = useState(0);
  const diskRef = useRef<HTMLDivElement>(null);
  const spinStartedAtMs = useRef(0);
  const finishedOnce = useRef(false);

  const winnerRef = useRef<FortuneWheelSectionItem | null>(null);
  useEffect(() => {
    winnerRef.current = winner;
  }, [winner]);

  const total = Math.max(1, sections.length || 1);
  const arc = 360 / total;

  const setDiskTransition = (ms: number, easing: string) => {
    const el = diskRef.current;
    if (el) {
      el.style.transition = `transform ${ms}ms ${easing}`;
    }
  };

  const finalizeAfterAnimation = (win: FortuneWheelSectionItem) => {
    if (finishedOnce.current) {
      return;
    }
    finishedOnce.current = true;

    const el = diskRef.current;
    if (!el) {
      return;
    }

    el.style.transition = "none";
    el.getBoundingClientRect();
    setAngleDeg((a) => normalizeAngleDeg(a));

    requestAnimationFrame(() => {
      if (diskRef.current) {
        diskRef.current.style.transition = "";
      }
    });

    setStep(
      isWinningSection(win)
        ? FORTUNE_WHEEL_STEPS.SUCCESS
        : FORTUNE_WHEEL_STEPS.FAILED,
    );
  };

  async function spin() {
    if (!canSpin || step === FORTUNE_WHEEL_STEPS.SPIN || !sections.length) {
      return;
    }

    finishedOnce.current = false;
    setStep(FORTUNE_WHEEL_STEPS.SPIN);
    spinStartedAtMs.current = performance.now();

    const el = diskRef.current;
    if (!el) {
      return;
    }

    // фаза 1 — разгон
    const t1 = Math.max(
      MIN_PHASE_MS,
      Math.floor(SPIN_TOTAL_MS * DECELERATE_AT),
    );
    const delta1 = FAST_TURNS * 360;
    const afterFast = angleDeg + delta1;

    el.style.transition = "none";
    el.getBoundingClientRect();
    setDiskTransition(t1, "linear");
    el.getBoundingClientRect();
    setAngleDeg(afterFast);

    const rest = t1 - (performance.now() - spinStartedAtMs.current);
    if (rest > 0) {
      await new Promise((r) => {
        setTimeout(r, rest);
      });
    }

    const w = winnerRef.current;
    if (!w) {
      // аккуратно завершаем без докрутки и не меняем доступность до container-логики
      el.style.transition = "none";
      requestAnimationFrame(() => {
        if (diskRef.current) {
          diskRef.current.style.transition = "";
        }
      });
      setStep(FORTUNE_WHEEL_STEPS.FAILED);
      return;
    }

    const idx = findSectionIndexById(sections, w.id);
    if (idx < 0) {
      el.style.transition = "none";
      requestAnimationFrame(() => {
        if (diskRef.current) {
          diskRef.current.style.transition = "";
        }
      });
      setStep(FORTUNE_WHEEL_STEPS.FAILED);
      return;
    }

    // фаза 2 — замедление до центра целевого сектора
    const midNorm = normalizeAngleDeg(afterFast);
    const signedIdx = CLOCKWISE ? idx : -idx;
    const target = normalizeAngleDeg(TOP_OFFSET_DEG - signedIdx * arc);

    const deltaFromMid = shortestPositiveDelta(midNorm, target);
    const extraTurnsDeg = (TOTAL_TURNS - FAST_TURNS) * 360;
    const delta2 = extraTurnsDeg + deltaFromMid;

    const elapsed = performance.now() - spinStartedAtMs.current;
    const t2 = Math.max(MIN_PHASE_MS, SPIN_TOTAL_MS - elapsed);
    const easing2 = makeContinuousEaseOut(t1, delta1, t2, delta2);

    // one-shot завершение строго по окончанию CSS-перехода
    const onEnd = () => {
      el.removeEventListener("transitionend", onEnd);
      finalizeAfterAnimation(w);
    };
    el.addEventListener("transitionend", onEnd, { once: true });

    // страхующий таймаут на случай, если transitionend не выстрелит
    const fallback = window.setTimeout(() => {
      el.removeEventListener("transitionend", onEnd);
      finalizeAfterAnimation(w);
    }, t2 + 120);

    setDiskTransition(t2, easing2);
    el.getBoundingClientRect();
    setAngleDeg(afterFast + delta2);

    // очистка при размонтировании
    const cleanup = () => {
      el.removeEventListener("transitionend", onEnd);
      window.clearTimeout(fallback);
    };
    // @ts-expect-error
    spin.cleanup = cleanup;
  }

  useEffect(() => {
    return () => {
      // @ts-expect-error
      spin.cleanup?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { diskRef, angle: angleDeg, step, spin };
};
